import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useBranding } from '@/lib/branding';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Trash2, Plus, Save, Eye } from 'lucide-react';
import { CapinLogo } from '@/components/brand/CapinLogo';
import { toast } from 'sonner';

export default function AdminBranding() {
  const { branding: live, refresh } = useBranding();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (live) setForm({ ...live, stats: [...(live.stats || [])] }); }, [live?.brand_name]);

  if (!form) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateStat = (i, k, v) => setForm((f) => { const s = [...f.stats]; s[i] = { ...s[i], [k]: v }; return { ...f, stats: s }; });
  const addStat = () => setForm((f) => ({ ...f, stats: [...(f.stats || []), { value: '', label: '' }] }));
  const removeStat = (i) => setForm((f) => ({ ...f, stats: f.stats.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put('/branding', form);
      await refresh();
      toast.success('Branding updated. Login page now reflects your changes.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Branding" subtitle="Customize the public login page and footer for your business" actions={
        <div className="flex gap-2">
          <a href="/login" target="_blank" rel="noreferrer"><Button variant="outline"><Eye className="h-4 w-4 mr-1" /> Preview</Button></a>
          <Button onClick={save} disabled={busy} data-testid="branding-save-button"><Save className="h-4 w-4 mr-1" /> {busy ? 'Saving…' : 'Save changes'}</Button>
        </div>
      } />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-7 p-5 space-y-5">
          <section>
            <h3 className="text-sm font-semibold mb-3">Identity</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Brand name</Label><Input value={form.brand_name || ''} onChange={(e) => update('brand_name', e.target.value)} data-testid="brand-name-input" /></div>
              <div><Label>Brand suffix</Label><Input value={form.brand_suffix || ''} onChange={(e) => update('brand_suffix', e.target.value)} placeholder="e.g. Realty" /></div>
              <div><Label>City</Label><Input value={form.city || ''} onChange={(e) => update('city', e.target.value)} /></div>
              <div><Label>Footer text</Label><Input value={form.footer_text || ''} onChange={(e) => update('footer_text', e.target.value)} /></div>
              <div className="col-span-2"><Label>Location line (under logo)</Label><Input value={form.location_text || ''} onChange={(e) => update('location_text', e.target.value)} /></div>
            </div>
          </section>
          <section>
            <h3 className="text-sm font-semibold mb-3">Hero tagline</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Line 1</Label><Input value={form.tagline_top || ''} onChange={(e) => update('tagline_top', e.target.value)} data-testid="tagline-top-input" /></div>
              <div><Label>Line 2 (accent)</Label><Input value={form.tagline_accent || ''} onChange={(e) => update('tagline_accent', e.target.value)} /></div>
              <div className="col-span-2"><Label>Line 3</Label><Input value={form.tagline_bottom || ''} onChange={(e) => update('tagline_bottom', e.target.value)} /></div>
              <div className="col-span-2"><Label>Subtitle paragraph</Label><Textarea rows={3} value={form.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} data-testid="subtitle-input" /></div>
            </div>
          </section>
          <section>
            <h3 className="text-sm font-semibold mb-3">Stats</h3>
            <div className="space-y-2">
              {(form.stats || []).map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3"><Label className="text-xs">Value</Label><Input value={s.value} onChange={(e) => updateStat(i, 'value', e.target.value)} placeholder="500+" data-testid={`stat-value-${i}`} /></div>
                  <div className="col-span-8"><Label className="text-xs">Label</Label><Input value={s.label} onChange={(e) => updateStat(i, 'label', e.target.value)} placeholder="Properties Listed" data-testid={`stat-label-${i}`} /></div>
                  <Button size="sm" variant="ghost" className="col-span-1" onClick={() => removeStat(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addStat} data-testid="add-stat-button"><Plus className="h-3.5 w-3.5 mr-1" /> Add stat</Button>
            </div>
          </section>
          <section>
            <h3 className="text-sm font-semibold mb-3">Visuals</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Logo monogram (e.g. CR)</Label><Input value={form.logo_monogram || ''} onChange={(e) => update('logo_monogram', e.target.value)} maxLength={3} /></div>
              <div><Label>Logo image URL (optional)</Label><Input value={form.logo_url || ''} onChange={(e) => update('logo_url', e.target.value)} placeholder="https://..." data-testid="logo-url-input" /></div>
              <div className="col-span-2"><Label>Background image URL</Label><Input value={form.background_url || ''} onChange={(e) => update('background_url', e.target.value)} placeholder="https://..." data-testid="background-url-input" /></div>
            </div>
          </section>
        </Card>

        {/* Live preview */}
        <Card className="lg:col-span-5 p-0 overflow-hidden">
          <div className="text-xs px-3 py-2 bg-slate-50 border-b text-slate-500">Live preview</div>
          <div className="relative aspect-[4/5]">
            <div className="absolute inset-0" style={{ backgroundImage: `url(${form.background_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(10,37,64,0.85) 0%, rgba(10,37,64,0.55) 100%)' }} />
            <div className="relative h-full flex flex-col p-6 text-white">
              <div className="flex items-center gap-2">
                <CapinLogo size={42} monogram={form.logo_monogram} imageUrl={form.logo_url} />
                <div className="text-base font-semibold">{form.brand_name} <span style={{ color: '#d4a017' }}>{form.brand_suffix}</span></div>
              </div>
              <div className="mt-auto">
                <div className="font-display text-3xl font-semibold leading-tight">
                  <div>{form.tagline_top}</div>
                  <div style={{ color: '#d4a017' }}>{form.tagline_accent}</div>
                  <div>{form.tagline_bottom}</div>
                </div>
                <p className="text-xs text-white/80 mt-3">{form.subtitle}</p>
                <div className="grid grid-cols-3 gap-3 mt-5">
                  {(form.stats || []).map((s, i) => (
                    <div key={i}>
                      <div className="font-display text-xl font-semibold" style={{ color: '#d4a017' }}>{s.value}</div>
                      <div className="text-[10px] text-white/80">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
